import { z } from "zod";
import { wikiPassageAnchorSchema } from "./wiki-passage.ts";
import { wikiCommentSchema, wikiThreadKindSchema, wikiThreadStatusSchema } from "./wiki-threads.ts";

// The history of a passage and the two "changes since" views (wiki-collaboration-ux S5, D17,
// D18). Every element is what the viewer may read: a hidden version, set, item or thread adds
// nothing, and a name the viewer may not see is null (the client reads "a member").

/** A passage of a published page whose history to read: its version (the current one when absent). */
export const wikiPassageHistoryRequestSchema = z.object({
  version: z.int().min(1).optional(),
  anchor: wikiPassageAnchorSchema,
});
export type WikiPassageHistoryRequest = z.infer<typeof wikiPassageHistoryRequestSchema>;

/** A thread of the producing set on the passage, with its decision note (D16). */
export const wikiPassageHistoryThreadSchema = z.object({
  id: z.uuid(),
  kind: wikiThreadKindSchema,
  status: wikiThreadStatusSchema,
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  resolvedByName: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  resolutionNote: z.string().nullable(),
  /** The passage it was opened on, as the item read then. */
  quote: z.string().nullable(),
  comments: z.array(wikiCommentSchema.omit({ mayDelete: true })),
});
export type WikiPassageHistoryThread = z.infer<typeof wikiPassageHistoryThreadSchema>;

export const wikiPassageHistorySchema = z.object({
  /**
   * The version whose publication introduced the passage, found by walking back the versions
   * the viewer may read; null when a version on the way is not theirs to read or the walk's
   * bound was reached (nothing older is named).
   */
  origin: z
    .object({
      version: z.int().min(1),
      publishedAt: z.string(),
      authorName: z.string().nullable(),
      publishedByName: z.string().nullable(),
    })
    .nullable(),
  /** The change set that published it, when the viewer may open it (the feed rule, D5). */
  set: z
    .object({
      id: z.uuid(),
      title: z.string(),
      createdByName: z.string().nullable(),
      approvedByName: z.string().nullable(),
      publishedByName: z.string().nullable(),
    })
    .nullable(),
  /** That set's threads on the passage, resolved and rejected ones included, oldest first. */
  threads: z.array(wikiPassageHistoryThreadSchema),
});
export type WikiPassageHistory = z.infer<typeof wikiPassageHistorySchema>;

// ── Since my last review (D18) ─────────────────────────────────────────────────────────────

export const wikiReviewDecisionSchema = z.enum(["changes_requested", "approved", "rejected"]);
export type WikiReviewDecision = z.infer<typeof wikiReviewDecisionSchema>;

export const wikiReviewSinceItemSchema = z.object({
  itemId: z.uuid(),
  /** `new`: added since the viewer's last mark on the set (an item they did not decide on has no line). */
  state: z.enum(["revised", "unchanged", "new"]),
  decision: wikiReviewDecisionSchema.nullable(),
  markedAt: z.string().nullable(),
  /** The item as the viewer reviewed it, for a revised item; null otherwise. */
  reviewed: z.object({ title: z.string(), markdown: z.string().nullable() }).nullable(),
});
export type WikiReviewSinceItem = z.infer<typeof wikiReviewSinceItemSchema>;

/** The viewer's own marks on a set, for the items they may see; empty when they marked none. */
export const wikiReviewSinceSchema = z.object({
  items: z.array(wikiReviewSinceItemSchema),
});
export type WikiReviewSince = z.infer<typeof wikiReviewSinceSchema>;

// ── Since I last read it (D18) ─────────────────────────────────────────────────────────────

export const wikiPageReadRequestSchema = z.object({ version: z.int().min(1) });
export type WikiPageReadRequest = z.infer<typeof wikiPageReadRequestSchema>;

/**
 * The version to compare the page against: the newest the viewer may read at or before their
 * previous read, when older than the version they read now; null otherwise.
 */
export const wikiPageReadSchema = z.object({ since: z.int().min(1).nullable() });
export type WikiPageRead = z.infer<typeof wikiPageReadSchema>;
